import { Icon } from "@iconify/react";
import { Box, Grid } from "@mui/material";
import React from "react";
import AppDialog from "../../components/Common/AppDialog";
import Label from "../../components/Common/Label";
import CustomButton from "../../components/Form/CustomButton";
import moment from "moment";

const InventoryDetailsDialog = ({
  open,
  close,
  item,
  status,
  statusList,
  isMobile,
  onDelete,
  onEdit,
}) => {
  const displayValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "object") {
      return value.name || value.label || value.creator?.name || fallback;
    }
    return value;
  };
  const money = (value) =>
    `$${Number(value || 0).toLocaleString("en-SG", { minimumFractionDigits: 2 })}`;
  const isSingPass = Boolean(item?.myinfo_make || item?.myinfo_model);

  const detailRow = (label, value) => (
    <Grid container item xs={12} spacing={1} alignItems="center">
      <Grid item xs={6} className="subheader-2">
        {label}
      </Grid>
      <Grid
        item
        xs={6}
        textAlign="right"
        sx={{ color: "#808588" || "inherit" }}
      >
        {value ?? "-"}
      </Grid>
    </Grid>
  );

  if (!item) return null;

  return (
    <AppDialog
      open={open}
      close={close}
      title="Vehicle Details"
      width="75%"
      hideCloseIcon={false}
      isMobile={isMobile}
      mobileSizeModalMaxHeight="92vh"
      contentSx={{ pb: 1 }}
      customTitle={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          {item?.source === "Manual" && (
            <CustomButton
              title={
                <>
                  <Icon
                    icon="solar:trash-bin-trash-linear"
                    style={{ marginRight: 8 }}
                  />
                  Delete
                </>
              }
              press={onDelete}
              backgroundColor="#FFFFFF"
              textColor="#ED1A3D"
              borderStyle="1px solid #ED1A3D"
              height={34}
            />)}

          {displayValue(item?.source, "Manual") === "Manual" && (
            <CustomButton
              title={
                <>
                  <Icon icon="ri:pencil-line" style={{ marginRight: 8 }} />
                  Edit
                </>
              }
              press={onEdit}
              height={34}
            />
          )}
        </Box>
      }
    >
      <div className="text-semibold mb-2">
        {item.vehicle || `${item.make || ""} ${item.model || ""}`}
      </div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2.5,
        }}
      >
        {status && (
          <Label.GeneralStatusLabel
            label={status}
            list={statusList}
            marginLeft="0"
          />
        )}
        {item.manu_year && <>
          {status && <span className="subheader-2">•</span>}
          <span className="subheader-2">{item.manu_year || ""}</span>
        </>}
        {item.inventory_type && <>
          {(status || item.manu_year) && <span className="subheader-2">•</span>}
          <span
            style={{ color: item.inventory_type?.toLowerCase() === "new" ? "#2777C8" : "#FF7900" }}
          >
            {item.inventory_type?.toLowerCase() === "new"
              ? "New"
              : item.inventory_type?.toLowerCase() === "rental"
                ? "Rental"
                : "Used"}
          </span>
        </>
        }
        {item.body_type && (
          <>
            {(status || item.manu_year || item.inventory_type) && <span className="subheader-2">•</span>}
            <span className="subheader-2">{item.body_type}</span>
          </>
        )}
        {item.engine_cc &&
          <>
            {(status || item.manu_year || item.inventory_type || item.body_type) && <span className="subheader-2">•</span>}
            <span className="subheader-2">{item.engine_cc || ""}cc</span>
          </>}
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: "#F7F7F7",
          borderRadius: "12px",
          py: 2.5,
          mb: 3,
        }}
      >
        {item?.inventory_type?.toLowerCase() !== "new" ?
          <Grid item xs={12} sm={4} textAlign="center" sx={{ py: 0.5 }}>
            <div className="text-bold font-size-20">
              {item.register_number || "-"}
            </div>
            <div className="subheader-2 font-size-12 mt-1">Registration No.</div>
          </Grid> :
          <Grid item xs={12} sm={4} textAlign="center" sx={{ py: 0.5 }}>
            <div className="text-bold font-size-20">
              {item.manu_year || "-"}
            </div>
            <div className="subheader-2 font-size-12 mt-1">Year of Manufacturer</div>
          </Grid>
        }
        {item?.inventory_type?.toLowerCase() === "rental" ?
          <>
            <Grid item xs={6} sm={4} textAlign="center" sx={{ py: 0.5 }}>
              <div className="text-bold font-size-20">
                {money(item.rental_rates?.daily_rate)}
              </div>
              <div className="subheader-2 font-size-12 mt-1">Daily Rate</div>
            </Grid>
            <Grid item xs={6} sm={4} textAlign="center" sx={{ py: 0.5 }}>
              <div className="text-bold font-size-20">{money(item.rental_rates?.weekend_rate)}</div>
              <div className="subheader-2 font-size-12 mt-1">Weekend Rate</div>
            </Grid>
          </>
          : <>
            <Grid item xs={6} sm={4} textAlign="center" sx={{ py: 0.5 }}>
              <div className="text-bold font-size-20">
                {money(item.purchase_price)}
              </div>
              <div className="subheader-2 font-size-12 mt-1">Purchase Price</div>
            </Grid>
            <Grid item xs={6} sm={4} textAlign="center" sx={{ py: 0.5 }}>
              <div className="text-bold font-size-20">{money(item.suggested_price)}</div>
              <div className="subheader-2 font-size-12 mt-1">Suggested Price</div>
            </Grid>
          </>}
      </Grid>

      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid item xs={12} md={6}>
          <div className="text-semibold mb-3">Vehicle</div>
          <Grid container spacing={2.25}>
            {isSingPass && (
              <>
                {detailRow("Make (Myinfo)", item.myinfo_make)}
                {detailRow("Model (Myinfo)", item.myinfo_model)}
              </>
            )}
            {detailRow("Make & model", item.vehicle)}
            {detailRow("Body type", item.body_type)}
            {detailRow("Year of manufacture", item.manu_year)}
            {detailRow("Engine capacity", `${item.engine_cc || "-"}cc`)}
            {detailRow("Primary color", item.color)}
            {detailRow(
              "Inventory type",
              item.inventory_type?.toLowerCase() === "new"
                ? "New"
                : item.inventory_type?.toLowerCase() === "rental"
                  ? "Rental"
                  : "Used",
              item.inventory_type?.toLowerCase() === "new" ? "#2777C8" : "#FF7900",
            )}
            {detailRow("Source", displayValue(item.source, "Manual"))}
            {item?.inventory_type?.toLowerCase() !== "new" && detailRow(
              "Mileage",
              item.mileage_in_km
                ? `${Number(item.mileage_in_km).toLocaleString()} km`
                : "- km",
            )}
            {detailRow(
              "Stock Status",
              <div className="d-flex justify-content-end align-items-center">
                <Label.GeneralStatusLabel
                  label={status}
                  list={statusList}
                  marginLeft="0"
                />
              </div>,
            )}
            {item?.inventory_type?.toLowerCase() !== "new" && detailRow(
              "No. of owner",
              item.no_of_previous_owners
                ? `${item.no_of_previous_owners} owner${Number(item.no_of_previous_owners) === 1 ? "" : "s"}`
                : "-",
            )}
            {item?.inventory_type?.toLowerCase() === "used" && (
              <>
                {detailRow("Owner name", item.owner_name)}
                {detailRow("Owner email", item.owner_email)}
                {detailRow("Owner phone", item.owner_phone)}
              </>
            )}

          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="text-semibold mb-3">Registration & COE</div>
          <Grid container spacing={2.25}>
            {item?.inventory_type?.toLowerCase() !== "new" && detailRow("Registration no.", item.register_number)}
            {detailRow("Revision", item.revision)}
            {item?.inventory_type?.toLowerCase() === "used" &&
              <>
                {detailRow("Registration date", item.registration_date ? moment(item.registration_date).format("DD MMM YYYY") : "-")}
                {detailRow("COE expiry", item.coe_expiry ? moment(item.coe_expiry).format("DD MMM YYYY") : "-")}
                {detailRow("COE category", item.coe_category)}
              </>
            }

          </Grid>
          <div className="text-semibold mt-4 mb-3">Pricing Breakdown</div>
          {item?.inventory_type?.toLowerCase() === "rental" ? (
            <Grid container spacing={2.25}>
              {detailRow("Daily rate", money(item.rental_rates?.daily_rate))}
              {detailRow("Weekend rate", money(item.rental_rates?.weekend_rate))}
              {detailRow("3 days rate", money(item.rental_rates?.["3_days_rate"]))}
              {detailRow("Weekly rate", money(item.rental_rates?.weekly_rate))}
              {detailRow("Monthly rate", money(item.rental_rates?.monthly_rate))}
            </Grid>
          ) : (
            <Grid container spacing={2.25}>
              {detailRow("Purchase price", money(item.purchase_price))}
              {detailRow("Suggested price", money(item.suggested_price))}
              {item?.inventory_type?.toLowerCase() !== "used" && detailRow("ARF", money(item.arf))}
            </Grid>
          )}
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, mb: 2 }}>
        <div className="text-semibold mb-3">Remarks</div>
        <div className="subheader-2">{item.internal_remarks || "-"}</div>
      </Box>
    </AppDialog>
  );
};

export default InventoryDetailsDialog;
